/**
 * ─── LAYER: lib ──────────────────────────────────────────────────────────────────
 *
 * ⚠️ [D4.5 §1b] NO LABEL LOOKUP IN THIS APPLICATION MAY FALL THROUGH TO AN ID.
 *
 * ⚠️ THE MECHANISM, READ OUT OF THE LIBRARY RATHER THAN ASSUMED. The last step of
 * `rc-select@14.16.8/lib/Select.js` when it builds what to display is
 *
 *     label: (labelRender ? labelRender(item) : item.label) ?? item.value
 *
 * and `rc-tree-select@5.27.0/lib/TreeSelect.js` does the identical thing. So a `value` with
 * no matching entry in `options` renders as the RAW VALUE — a 24-character ObjectId, on
 * screen, in front of a client. Nothing warns; the control looks like it is working.
 *
 * ⚠️ IT IS A MOUNT-TIME FAULT, NOT AN INTERACTION ONE, AND THAT IS WHY IT SURVIVED.
 * rc-select's `useCache` (`lib/hooks/useCache.js`) remembers a label it has already
 * resolved, so choosing a value and then watching the option list change is SAFE — which is
 * every path anyone exercises by hand. What breaks is opening a SAVED record whose stored id
 * is absent from the first option payload, and there are three ordinary ways for that to
 * happen:
 *
 *   1. THE ROW WAS ARCHIVED OR DEACTIVATED. Most masters are fetched active-only, so a
 *      policy version naming a retired rank has a value its options will never contain.
 *   2. THE LIST IS PAGINATED OR SEARCH-LIMITED. `/officers` returns twenty; a command role's
 *      stored officers are essentially never among the first twenty unsearched rows.
 *   3. THE VALUE IS OUTSIDE THE CALLER'S SCOPE. `/org-units/tree` is scoped server-side, so
 *      an id from an order raised elsewhere is simply not in the tree.
 *
 * ⚠️ AND NOTHING HERE WIDENS WHAT MAY BE CHOSEN. A fallback entry is only ever the value
 * ALREADY STORED on the record — the same distinction `lib/policyColumns.ts` draws between
 * `postRows` (what a new order is OFFERED) and `allPostTypes` (what an existing one RESOLVES
 * its labels from). Filtering the second is the silent regression; this is its general form.
 *
 * ⚠️ WHY NOT `labelInValue`. It would store the label alongside the id on the wire, which
 * makes a stale label indistinguishable from a current one and puts a display string into a
 * payload the API validates. `labelRender` was the other candidate and is worse here: it
 * fires for every entry, so it cannot say "this one is missing" without re-implementing the
 * lookup anyway. Merging into `options` keeps one code path and leaves the wire alone.
 */

export type SelectOption = { value: string; label: string };

/** Just enough of a master row to name it. Most summary DTOs satisfy this structurally. */
export type Named = { name: string };

/**
 * What is missing, said out loud.
 *
 * ⚠️ NOT AN EM DASH, AND NOT A BLANK. `toGrid` already uses this exact phrase for a column
 * whose master row has gone, and it is the right register: a dash reads as "nothing here",
 * which is a different fact from "something here, and it is no longer available". An
 * operator seeing this knows the row is stale rather than reading a hex string as data.
 */
export const NOT_AVAILABLE = 'No longer available';

/**
 * One chosen value, guaranteed to have an option.
 *
 * `resolve` is consulted only when the value is missing from `options`, so the ordinary path
 * costs one `some()` and allocates nothing new.
 */
export const withChosenOption = <T extends SelectOption>(
  options: readonly T[],
  chosen: string | null | undefined,
  resolve?: (id: string) => Named | undefined,
): (T | SelectOption)[] => (chosen ? withChosenOptions(options, [chosen], resolve) : [...options]);

/**
 * Several chosen values, each guaranteed an option.
 *
 * ⚠️ THE FALLBACKS GO AT THE END, in the order they were chosen. Sorting them in among the
 * live rows would put a row nobody may pick in the middle of a list of rows they may.
 */
export const withChosenOptions = <T extends SelectOption>(
  options: readonly T[],
  chosen: readonly (string | null | undefined)[],
  resolve?: (id: string) => Named | undefined,
): (T | SelectOption)[] => {
  const present = new Set(options.map((option) => option.value));
  const missing: SelectOption[] = [];

  for (const id of chosen) {
    // `null`/`''` are real values in some pickers ("Any unit"); an absent one is not missing.
    if (!id || present.has(id)) continue;
    present.add(id);
    const known = resolve?.(id);
    missing.push({ value: id, label: known ? known.name : NOT_AVAILABLE });
  }

  return missing.length === 0 ? [...options] : [...options, ...missing];
};

/**
 * The same guarantee for a display string that is not inside a `Select` — a chip, a
 * checkbox label, a table cell.
 *
 * ⚠️ IT EXISTS BECAUSE `?? id` IS THE SAME BUG WITHOUT THE LIBRARY. `Sections.tsx`'s
 * per-slot command picker read `officers.get(officerId)?.name ?? officerId`, which puts an
 * ObjectId beside a checkbox for exactly the reasons above. A `??` onto the id is never the
 * right fallback, and this is what to reach for instead.
 */
export const nameOr = (known: Named | undefined, fallback: string = NOT_AVAILABLE): string => known?.name ?? fallback;
